import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";

// ── Types ─────────────────────────────────────────────────────
interface ScenarioSlide {
  type: "content" | "steps" | "highlight";
  heading: string;
  body?: string;
  intro?: string;
  warning?: string;
  points?: string[];
  steps?: { label: string; text: string }[];
  highlights?: { letter: string; word: string; text: string }[];
  contact?: { label: string; email: string; phone: string };
}

interface ScenarioQuiz {
  question: string;
  options: string[];
  correct: number;
  explanation: string;
}

interface ScenarioModule {
  id: string;
  number: number;
  title: string;
  icon: string;
  color: string;
  scenario: { character: string; text: string; prompt: string };
  slides: ScenarioSlide[];
  quiz: ScenarioQuiz;
}

interface ScenarioConfig {
  title: string;
  subtitle: string;
  organization: string;
  passMark: number;
  version: string;
}

interface ScenarioCoursePlayerProps {
  courseId: string;
  courseTitle: string;
  config: ScenarioConfig;
  modules: ScenarioModule[];
  isAlreadyCompleted?: boolean;
}

// ── Lurneax Design tokens ─────────────────────────────────────
const T = {
  bg: "hsl(200, 15%, 10%)",
  surface: "hsl(200, 15%, 14%)",
  surfaceHigh: "hsl(200, 15%, 18%)",
  border: "hsl(200, 12%, 26%)",
  borderLight: "hsl(200, 10%, 32%)",
  text: "hsl(200, 10%, 90%)",
  textMuted: "hsl(200, 10%, 58%)",
  textFaint: "hsl(200, 10%, 38%)",
  accent: "hsl(145, 30%, 45%)",          // Organic Green
  accentGlow: "hsla(145, 30%, 45%, 0.15)",
  accentAlt: "hsl(45, 55%, 58%)",        // Muted Gold
  primary: "hsl(200, 15%, 42%)",         // Metallic Chrome
  success: "hsl(145, 30%, 45%)",
  successBg: "hsla(145, 30%, 45%, 0.1)",
  warning: "hsl(45, 55%, 58%)",
  warningBg: "hsla(45, 55%, 58%, 0.1)",
  danger: "#F44321",                     // Email Red
  dangerBg: "rgba(244, 67, 33, 0.1)",
  white: "#ffffff",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700;800&family=Inter:ital,wght@0,300;0,400;0,500;1,400&display=swap');

  .cp-wrap * { box-sizing: border-box; margin: 0; padding: 0; }
  .cp-wrap { font-family: 'Inter', sans-serif; background: ${T.bg}; color: ${T.text}; min-height: 100vh; }

  @keyframes cp-fadein { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
  @keyframes cp-fadein-fast { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
  @keyframes cp-pop { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
  @keyframes cp-progress { from { width: 0; } }

  .cp-anim { animation: cp-fadein 0.38s cubic-bezier(0.22,1,0.36,1) both; }
  .cp-anim-fast { animation: cp-fadein-fast 0.22s ease both; }
  .cp-pop { animation: cp-pop 0.32s cubic-bezier(0.22,1,0.36,1) both; }

  .cp-shell { display: grid; grid-template-columns: 280px 1fr; min-height: 100vh; }
  @media (max-width: 900px) { .cp-shell { grid-template-columns: 1fr; } }

  .cp-sidebar {
    background: ${T.surface};
    border-right: 1px solid ${T.border};
    padding: 0;
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    height: 100vh;
    overflow-y: auto;
  }
  .cp-sidebar-header {
    padding: 28px 24px 20px;
    border-bottom: 1px solid ${T.border};
  }
  .cp-logo { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 18px; letter-spacing: -0.02em; color: ${T.white}; }
  .cp-course-meta { font-size: 11px; color: ${T.textFaint}; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.08em; }
  .cp-overall-progress { margin-top: 16px; }
  .cp-progress-label { display: flex; justify-content: space-between; font-size: 11px; color: ${T.textMuted}; margin-bottom: 6px; }
  .cp-progress-bar { height: 3px; background: ${T.border}; border-radius: 2px; overflow: hidden; }
  .cp-progress-fill { height: 100%; background: linear-gradient(135deg, ${T.accent}, ${T.accentAlt}); border-radius: 2px; transition: width 0.5s cubic-bezier(0.22,1,0.36,1); animation: cp-progress 0.6s ease; }

  .cp-nav { padding: 12px 0; flex: 1; }
  .cp-nav-item {
    display: flex; align-items: center; gap: 12px;
    padding: 11px 24px;
    cursor: pointer;
    transition: background 0.15s;
    position: relative;
    border-left: 3px solid transparent;
  }
  .cp-nav-item:hover { background: ${T.surfaceHigh}; }
  .cp-nav-item.active { background: ${T.accentGlow}; border-left-color: ${T.accent}; }
  .cp-nav-item.locked { opacity: 0.35; cursor: not-allowed; }
  .cp-nav-icon { font-size: 18px; flex-shrink: 0; width: 28px; text-align: center; }
  .cp-nav-text { flex: 1; min-width: 0; }
  .cp-nav-num { font-size: 10px; color: ${T.textFaint}; text-transform: uppercase; letter-spacing: 0.07em; }
  .cp-nav-title { font-size: 13px; font-weight: 500; color: ${T.text}; line-height: 1.3; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .cp-nav-status { flex-shrink: 0; width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid ${T.border}; display: flex; align-items: center; justify-content: center; font-size: 10px; }
  .cp-nav-status.done { background: ${T.success}; border-color: ${T.success}; color: #0f1117; }
  .cp-nav-status.active { border-color: ${T.accent}; }

  @media (max-width: 900px) {
    .cp-sidebar { position: static; height: auto; }
    .cp-nav { display: flex; overflow-x: auto; padding: 8px 12px; gap: 8px; }
    .cp-nav-item { padding: 8px 14px; border-left: none; border-bottom: 3px solid transparent; border-radius: 8px; flex-shrink: 0; }
    .cp-nav-item.active { border-bottom-color: ${T.accent}; }
    .cp-nav-text { display: none; }
    .cp-nav-status { display: none; }
  }

  .cp-main { overflow-y: auto; display: flex; flex-direction: column; }

  .cp-module-header {
    padding: 40px 48px 32px;
    border-bottom: 1px solid ${T.border};
    background: linear-gradient(180deg, ${T.surfaceHigh} 0%, transparent 100%);
  }
  @media (max-width: 700px) { .cp-module-header { padding: 24px 20px; } }
  .cp-module-tag { font-size: 11px; color: ${T.textFaint}; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 10px; }
  .cp-module-title { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 32px; letter-spacing: -0.03em; color: ${T.white}; line-height: 1.15; }
  @media (max-width: 700px) { .cp-module-title { font-size: 24px; } }
  .cp-module-subtitle { font-size: 14px; color: ${T.textMuted}; margin-top: 8px; }

  .cp-slide-dots { display: flex; gap: 6px; padding: 20px 48px 0; }
  @media (max-width: 700px) { .cp-slide-dots { padding: 16px 20px 0; } }
  .cp-dot { height: 3px; border-radius: 2px; transition: all 0.3s; background: ${T.border}; flex: 1; max-width: 40px; }
  .cp-dot.done { background: ${T.success}; }
  .cp-dot.active { background: ${T.accent}; }

  .cp-content { padding: 32px 48px 40px; flex: 1; max-width: 820px; }
  @media (max-width: 700px) { .cp-content { padding: 20px; } }

  .cp-scenario {
    background: linear-gradient(135deg, hsla(145, 30%, 45%, 0.08), hsla(45, 55%, 58%, 0.05));
    border: 1px solid hsla(145, 30%, 45%, 0.25);
    border-radius: 12px;
    padding: 28px 32px;
    margin-bottom: 32px;
    position: relative;
    overflow: hidden;
  }
  .cp-scenario::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(135deg, ${T.accent}, ${T.accentAlt});
  }
  .cp-scenario-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: ${T.accent}; margin-bottom: 12px; }
  .cp-scenario-text { font-size: 15px; line-height: 1.7; color: ${T.text}; }
  .cp-scenario-prompt { font-size: 14px; color: ${T.accent}; font-style: italic; margin-top: 14px; font-weight: 500; }

  .cp-slide-heading { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 22px; color: ${T.white}; letter-spacing: -0.02em; margin-bottom: 20px; }

  .cp-warning {
    background: ${T.warningBg};
    border: 1px solid rgba(245,158,11,0.3);
    border-radius: 10px;
    padding: 14px 18px;
    font-size: 13px;
    color: ${T.warning};
    margin-bottom: 20px;
    display: flex; gap: 10px; align-items: flex-start;
  }

  .cp-points { display: flex; flex-direction: column; gap: 10px; margin-top: 4px; }
  .cp-point {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px;
    background: ${T.surfaceHigh};
    border: 1px solid ${T.border};
    border-radius: 10px;
    font-size: 14px;
    line-height: 1.55;
    color: ${T.text};
    animation: cp-fadein-fast 0.25s ease both;
  }
  .cp-point-dot { width: 6px; height: 6px; border-radius: 50%; background: ${T.accent}; flex-shrink: 0; margin-top: 7px; }

  .cp-steps { display: flex; flex-direction: column; gap: 0; margin-top: 4px; }
  .cp-step { display: flex; gap: 16px; position: relative; padding-bottom: 4px; animation: cp-fadein-fast 0.25s ease both; }
  .cp-step-line { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
  .cp-step-num { width: 34px; height: 34px; border-radius: 50%; background: ${T.accent}; color: ${T.white}; font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 13px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .cp-step-connector { width: 2px; flex: 1; background: ${T.border}; margin: 4px 0; min-height: 20px; }
  .cp-step:last-child .cp-step-connector { display: none; }
  .cp-step-body { padding: 6px 0 20px; }
  .cp-step-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: ${T.accent}; margin-bottom: 4px; }
  .cp-step-text { font-size: 14px; line-height: 1.6; color: ${T.text}; }

  .cp-highlights { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 4px; }
  @media (max-width: 600px) { .cp-highlights { grid-template-columns: 1fr; } }
  .cp-highlight-card {
    background: ${T.surfaceHigh};
    border: 1px solid ${T.border};
    border-radius: 12px;
    padding: 20px;
    animation: cp-fadein-fast 0.25s ease both;
  }
  .cp-hl-letter { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 36px; color: ${T.accent}; line-height: 1; margin-bottom: 4px; }
  .cp-hl-word { font-size: 13px; font-weight: 600; color: ${T.white}; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em; }
  .cp-hl-text { font-size: 13px; color: ${T.textMuted}; line-height: 1.5; }

  .cp-contact { margin-top: 20px; background: ${T.surfaceHigh}; border: 1px solid ${T.border}; border-radius: 10px; padding: 16px 20px; }
  .cp-contact-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: ${T.textFaint}; margin-bottom: 8px; }
  .cp-contact-line { font-size: 14px; color: ${T.text}; margin-bottom: 4px; }
  .cp-contact-line a { color: ${T.accent}; text-decoration: none; }

  .cp-nav-buttons { display: flex; justify-content: space-between; align-items: center; padding: 24px 48px 40px; border-top: 1px solid ${T.border}; gap: 16px; }
  @media (max-width: 700px) { .cp-nav-buttons { padding: 16px 20px; } }
  .cp-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 12px 24px;
    border-radius: 10px;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.18s;
    text-decoration: none;
  }
  .cp-btn-ghost { background: transparent; color: ${T.textMuted}; border: 1px solid ${T.border}; }
  .cp-btn-ghost:hover { background: ${T.surfaceHigh}; color: ${T.text}; }
  .cp-btn-primary { background: ${T.accent}; color: white; }
  .cp-btn-primary:hover { background: hsl(145, 30%, 38%); transform: translateY(-1px); box-shadow: 0 4px 20px hsla(145, 30%, 45%, 0.35); }
  .cp-btn:disabled { opacity: 0.3; cursor: not-allowed; transform: none !important; }
  .cp-btn-success { background: ${T.success}; color: #0f1117; font-weight: 600; }
  .cp-btn-success:hover { background: #2fc98e; transform: translateY(-1px); }

  .cp-quiz-wrap { padding: 32px 48px 0; max-width: 820px; }
  @media (max-width: 700px) { .cp-quiz-wrap { padding: 20px 20px 0; } }
  .cp-quiz-header { font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: 0.1em; color: ${T.accent}; margin-bottom: 16px; }
  .cp-quiz-q { font-size: 17px; font-weight: 500; color: ${T.white}; line-height: 1.5; margin-bottom: 20px; }
  .cp-quiz-options { display: flex; flex-direction: column; gap: 10px; }
  .cp-quiz-option {
    display: flex; align-items: center; gap: 14px;
    padding: 16px 20px;
    background: ${T.surfaceHigh};
    border: 1.5px solid ${T.border};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.18s;
    font-size: 14px;
    line-height: 1.5;
    color: ${T.text};
    animation: cp-fadein-fast 0.25s ease both;
  }
  .cp-quiz-option:hover:not(.answered) { border-color: ${T.accent}; background: ${T.accentGlow}; }
  .cp-quiz-option.correct { border-color: ${T.success}; background: ${T.successBg}; }
  .cp-quiz-option.wrong { border-color: ${T.danger}; background: ${T.dangerBg}; }
  .cp-quiz-option.answered { cursor: not-allowed; }
  .cp-option-circle { width: 28px; height: 28px; border-radius: 50%; border: 1.5px solid ${T.border}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 12px; font-weight: 600; font-family: 'Space Grotesk', sans-serif; transition: all 0.18s; }
  .cp-quiz-option.correct .cp-option-circle { background: ${T.success}; border-color: ${T.success}; color: #0f1117; }
  .cp-quiz-option.wrong .cp-option-circle { background: ${T.danger}; border-color: ${T.danger}; color: white; }
  .cp-quiz-explain {
    margin-top: 16px;
    padding: 16px 20px;
    background: ${T.surfaceHigh};
    border: 1px solid ${T.border};
    border-left: 3px solid ${T.success};
    border-radius: 10px;
    font-size: 13px;
    color: ${T.textMuted};
    line-height: 1.6;
    animation: cp-fadein 0.3s ease;
  }
  .cp-quiz-explain strong { color: ${T.text}; }

  .cp-cert-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 80vh; padding: 40px 24px; text-align: center; }
  .cp-cert-badge { width: 100px; height: 100px; border-radius: 50%; background: linear-gradient(135deg, ${T.accent}, ${T.accentAlt}); display: flex; align-items: center; justify-content: center; font-size: 44px; margin: 0 auto 28px; box-shadow: 0 0 60px hsla(145, 30%, 45%, 0.4); animation: cp-pop 0.5s cubic-bezier(0.22,1,0.36,1); }
  .cp-cert-title { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 36px; color: ${T.white}; letter-spacing: -0.03em; margin-bottom: 8px; }
  .cp-cert-subtitle { font-size: 15px; color: ${T.textMuted}; margin-bottom: 32px; }
  .cp-cert-score { font-family: 'Space Grotesk', sans-serif; font-size: 64px; font-weight: 800; background: linear-gradient(135deg, ${T.accent}, ${T.accentAlt}); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; line-height: 1; margin-bottom: 8px; }
  .cp-cert-score-label { font-size: 13px; text-transform: uppercase; letter-spacing: 0.1em; color: ${T.textFaint}; margin-bottom: 32px; }
  .cp-cert-pass { display: inline-flex; align-items: center; gap: 8px; padding: 10px 24px; border-radius: 999px; font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; }
  .cp-cert-pass.pass { background: ${T.successBg}; color: ${T.success}; border: 1px solid rgba(52,211,153,0.3); }
  .cp-cert-pass.fail { background: ${T.dangerBg}; color: ${T.danger}; border: 1px solid rgba(248,113,113,0.3); }

  .cp-welcome { display: flex; flex-direction: column; justify-content: center; min-height: 80vh; padding: 48px; max-width: 680px; }
  @media (max-width: 700px) { .cp-welcome { padding: 32px 20px; } }
  .cp-welcome-tag { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.12em; color: ${T.accent}; margin-bottom: 20px; }
  .cp-welcome-title { font-family: 'Space Grotesk', sans-serif; font-weight: 800; font-size: 44px; letter-spacing: -0.04em; color: ${T.white}; line-height: 1.1; margin-bottom: 16px; }
  @media (max-width: 700px) { .cp-welcome-title { font-size: 32px; } }
  .cp-welcome-sub { font-size: 16px; line-height: 1.7; color: ${T.textMuted}; margin-bottom: 36px; max-width: 520px; }
  .cp-welcome-modules { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 40px; }
  .cp-welcome-mod { display: flex; align-items: center; gap: 6px; padding: 6px 12px; background: ${T.surfaceHigh}; border: 1px solid ${T.border}; border-radius: 999px; font-size: 12px; color: ${T.textMuted}; }

  .cp-sidebar::-webkit-scrollbar, .cp-main::-webkit-scrollbar { width: 4px; }
  .cp-sidebar::-webkit-scrollbar-track, .cp-main::-webkit-scrollbar-track { background: transparent; }
  .cp-sidebar::-webkit-scrollbar-thumb, .cp-main::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 2px; }

  .cp-back-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 16px;
    margin: 16px 24px 0;
    background: transparent;
    border: 1px solid ${T.border};
    border-radius: 8px;
    color: ${T.textMuted};
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s;
  }
  .cp-back-btn:hover { background: ${T.surfaceHigh}; color: ${T.text}; }
`;

// ── Slide renderer ────────────────────────────────────────────
function SlideContent({ slide }: { slide: ScenarioSlide }) {
  return (
    <div className="cp-anim">
      {slide.warning && (
        <div className="cp-warning">
          <span>⚠️</span>
          <span>{slide.warning}</span>
        </div>
      )}
      {slide.intro && (
        <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6, marginBottom: 20 }}>{slide.intro}</p>
      )}
      {slide.body && (
        <p style={{ fontSize: 15, color: T.text, lineHeight: 1.7, marginBottom: 20 }}>{slide.body}</p>
      )}

      {slide.type === "content" && slide.points && (
        <div className="cp-points">
          {slide.points.map((p, i) => (
            <div key={i} className="cp-point" style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="cp-point-dot" />
              <span>{p}</span>
            </div>
          ))}
        </div>
      )}

      {slide.type === "steps" && slide.steps && (
        <div className="cp-steps">
          {slide.steps.map((s, i) => (
            <div key={i} className="cp-step" style={{ animationDelay: `${i * 0.07}s` }}>
              <div className="cp-step-line">
                <div className="cp-step-num">{i + 1}</div>
                <div className="cp-step-connector" />
              </div>
              <div className="cp-step-body">
                <div className="cp-step-label">{s.label}</div>
                <div className="cp-step-text">{s.text}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {slide.type === "highlight" && slide.highlights && (
        <div className="cp-highlights">
          {slide.highlights.map((h, i) => (
            <div key={i} className="cp-highlight-card" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="cp-hl-letter">{h.letter}</div>
              <div className="cp-hl-word">{h.word}</div>
              <div className="cp-hl-text">{h.text}</div>
            </div>
          ))}
        </div>
      )}

      {slide.contact && (
        <div className="cp-contact">
          <div className="cp-contact-label">{slide.contact.label}</div>
          <div className="cp-contact-line">📧 <a href={`mailto:${slide.contact.email}`}>{slide.contact.email}</a></div>
          <div className="cp-contact-line">📞 <a href={`tel:${slide.contact.phone.replace(/\s/g, "")}`}>{slide.contact.phone}</a></div>
        </div>
      )}
    </div>
  );
}

// ── Quiz component ────────────────────────────────────────────
function Quiz({ quiz, onAnswer, answered }: { quiz: ScenarioQuiz; onAnswer: (i: number) => void; answered: number | null }) {
  const labels = ["A", "B", "C", "D"];
  return (
    <div className="cp-quiz-wrap cp-anim">
      <div className="cp-quiz-header">✅ Knowledge Check</div>
      <div className="cp-quiz-q">{quiz.question}</div>
      <div className="cp-quiz-options">
        {quiz.options.map((opt, i) => {
          let cls = "cp-quiz-option";
          if (answered !== null) {
            cls += " answered";
            if (i === quiz.correct) cls += " correct";
            else if (i === answered) cls += " wrong";
          }
          return (
            <div key={i} className={cls} onClick={() => answered === null && onAnswer(i)}
              style={{ animationDelay: `${i * 0.07}s` }}>
              <div className="cp-option-circle">
                {answered !== null && i === quiz.correct ? "✓" :
                 answered !== null && i === answered && i !== quiz.correct ? "✗" :
                 labels[i]}
              </div>
              <span>{opt}</span>
            </div>
          );
        })}
      </div>
      {answered !== null && (
        <div className="cp-quiz-explain">
          <strong>{answered === quiz.correct ? "Correct! " : "Not quite — "}</strong>
          <span dangerouslySetInnerHTML={{ __html: quiz.explanation || '' }} />
        </div>
      )}
    </div>
  );
}

// ── Certificate screen ────────────────────────────────────────
function Certificate({ score, passed, passMark, config, onRetry, onBackToCourses }: { 
  score: number; passed: boolean; passMark: number; config: ScenarioConfig; onRetry: () => void; onBackToCourses: () => void 
}) {
  return (
    <div className="cp-cert-wrap cp-anim">
      <div className="cp-cert-badge">{passed ? "🏆" : "📚"}</div>
      <div className="cp-cert-title">{passed ? "Course Complete!" : "Almost There"}</div>
      <div className="cp-cert-subtitle">
        {passed ? `${config.title} — ${config.organization}` : "Review the modules and try again"}
      </div>
      <div className="cp-cert-score">{score}%</div>
      <div className="cp-cert-score-label">Final Score</div>
      <div style={{ marginBottom: 32 }}>
        <span className={`cp-cert-pass ${passed ? "pass" : "fail"}`}>
          {passed ? "✓ Passed" : "✗ Did not pass"} — {passMark}% required
        </span>
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "center" }}>
        {!passed && (
          <button className="cp-btn cp-btn-primary" onClick={onRetry}>Retake Course →</button>
        )}
        <button className="cp-btn cp-btn-ghost" onClick={onBackToCourses}>← My Progress</button>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export function ScenarioCoursePlayer({ courseId, courseTitle, config, modules, isAlreadyCompleted }: ScenarioCoursePlayerProps) {
  const navigate = useNavigate();
  const exitPath = Capacitor.isNativePlatform() ? "/app/courses" : "/learning/courses";
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [screen, setScreen] = useState<"welcome" | "module" | "certificate">("welcome");
  const [activeModuleIdx, setActiveModuleIdx] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const [phase, setPhase] = useState<"scenario" | "slides" | "quiz">("scenario");
  const [quizAnswers, setQuizAnswers] = useState<Record<number, number>>({});
  const [completedModules, setCompletedModules] = useState<Set<number>>(new Set());
  const [isCompleting, setIsCompleting] = useState(false);

  const totalModules = modules.length;
  const progress = (completedModules.size / totalModules) * 100;
  const activeModule = modules[activeModuleIdx];
  const totalSlides = activeModule?.slides?.length ?? 0;
  const quizAnswered = quizAnswers[activeModuleIdx] ?? null;

  // Disable right-click
  useEffect(() => {
    const handler = (e: MouseEvent) => e.preventDefault();
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  }, []);

  const handleCompleteCourse = useCallback(async (score: number, passed: boolean) => {
    if (!passed) return;
    setIsCompleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase.from("profiles").select("full_name").eq("id", user.id).maybeSingle();

      const { data: existingEnrollment } = await supabase
        .from("course_enrollments")
        .select("id")
        .eq("course_id", courseId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (existingEnrollment) {
        await supabase.from("course_enrollments").update({ progress: 100, completed: true }).eq("id", existingEnrollment.id);
      } else {
        await supabase.from("course_enrollments").insert({ course_id: courseId, user_id: user.id, progress: 100, completed: true });
      }

      // Create certificate
      const { error: certError } = await supabase.from("certificates").insert({
        user_id: user.id,
        course_id: courseId,
        course_title: courseTitle,
        user_name: profile?.full_name || user.email || "Student",
        completion_date: new Date().toISOString()
      });

      if (certError && certError.code !== '23505') throw certError;

      queryClient.invalidateQueries({ queryKey: ["course-view", courseId] });
      queryClient.invalidateQueries({ queryKey: ["certificates"] });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsCompleting(false);
    }
  }, [courseId, courseTitle, queryClient, toast]);

  const handleNext = useCallback(() => {
    if (phase === "scenario") {
      setPhase("slides");
      setSlideIdx(0);
    } else if (phase === "slides") {
      if (slideIdx < totalSlides - 1) {
        setSlideIdx(i => i + 1);
      } else {
        setPhase("quiz");
      }
    } else if (phase === "quiz" && quizAnswered !== null) {
      const next = new Set(completedModules);
      next.add(activeModuleIdx);
      setCompletedModules(next);

      if (activeModuleIdx < totalModules - 1) {
        setActiveModuleIdx(i => i + 1);
        setPhase("scenario");
        setSlideIdx(0);
      } else {
        const correct = Object.entries({ ...quizAnswers }).filter(
          ([idx, ans]) => modules[parseInt(idx)].quiz.correct === ans
        ).length;
        const score = Math.round((correct / totalModules) * 100);
        const passed = score >= config.passMark;
        handleCompleteCourse(score, passed);
        setScreen("certificate");
      }
    }
  }, [phase, slideIdx, totalSlides, quizAnswered, activeModuleIdx, totalModules, completedModules, quizAnswers, modules, config.passMark, handleCompleteCourse]);

  const handlePrev = useCallback(() => {
    if (phase === "slides" && slideIdx > 0) setSlideIdx(i => i - 1);
    else if (phase === "slides" && slideIdx === 0) setPhase("scenario");
    else if (phase === "quiz") setPhase("slides");
  }, [phase, slideIdx]);

  const canGoNext = phase !== "quiz" || quizAnswered !== null;

  const finalScore = (() => {
    const correct = Object.entries(quizAnswers).filter(
      ([idx, ans]) => modules[parseInt(idx)]?.quiz?.correct === ans
    ).length;
    return Math.round((correct / totalModules) * 100);
  })();
  const passed = finalScore >= config.passMark;

  const handleRetry = () => {
    setScreen("welcome");
    setActiveModuleIdx(0);
    setSlideIdx(0);
    setPhase("scenario");
    setQuizAnswers({});
    setCompletedModules(new Set());
  };

  const slideProgress = phase === "scenario" ? 0 : phase === "quiz" ? totalSlides + 1 : slideIdx + 1;
  const totalSteps = totalSlides + 2;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div className="cp-wrap">
        {screen === "certificate" ? (
          <Certificate 
            score={finalScore} 
            passed={passed} 
            passMark={config.passMark}
            config={config}
            onRetry={handleRetry} 
            onBackToCourses={() => navigate("/learning/my-progress")}
          />
        ) : (
          <div className="cp-shell">
            {/* Sidebar */}
            <aside className="cp-sidebar">
              <button className="cp-back-btn" onClick={() => navigate(exitPath)}>
                ← Back to Courses
              </button>
              <div className="cp-sidebar-header">
                <div className="cp-logo">{config.organization}</div>
                <div className="cp-course-meta">{config.title}</div>
                {screen === "module" && (
                  <div className="cp-overall-progress">
                    <div className="cp-progress-label">
                      <span>Progress</span>
                      <span>{completedModules.size}/{totalModules}</span>
                    </div>
                    <div className="cp-progress-bar">
                      <div className="cp-progress-fill" style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                )}
              </div>
              <nav className="cp-nav">
                {modules.map((m, i) => {
                  const done = completedModules.has(i);
                  const active = screen === "module" && i === activeModuleIdx;
                  const locked = screen !== "module" || (i > activeModuleIdx && !completedModules.has(i));
                  return (
                    <div key={m.id}
                      className={`cp-nav-item ${active ? "active" : ""} ${locked ? "locked" : ""}`}
                      onClick={() => {
                        if (!locked) { setActiveModuleIdx(i); setPhase("scenario"); setSlideIdx(0); }
                      }}>
                      <div className="cp-nav-icon">{m.icon}</div>
                      <div className="cp-nav-text">
                        <div className="cp-nav-num">Module {m.number}</div>
                        <div className="cp-nav-title">{m.title}</div>
                      </div>
                      <div className={`cp-nav-status ${done ? "done" : active ? "active" : ""}`}>
                        {done ? "✓" : active ? "●" : ""}
                      </div>
                    </div>
                  );
                })}
              </nav>
            </aside>

            {/* Main */}
            <main className="cp-main">
              {screen === "welcome" ? (
                <div className="cp-welcome cp-anim">
                  <div className="cp-welcome-tag">{config.organization} · Mandatory Training</div>
                  <h1 className="cp-welcome-title">{config.title}</h1>
                  <p className="cp-welcome-sub">{config.subtitle}. Complete all {totalModules} modules and pass the knowledge checks to receive your completion certificate.</p>
                  <div className="cp-welcome-modules">
                    {modules.map(m => (
                      <div key={m.id} className="cp-welcome-mod">
                        <span>{m.icon}</span>
                        <span>{m.title}</span>
                      </div>
                    ))}
                  </div>
                  {isAlreadyCompleted && (
                    <div style={{ marginBottom: 16, padding: "10px 18px", background: T.successBg, border: `1px solid rgba(52,211,153,0.3)`, borderRadius: 10, color: T.success, fontSize: 13, fontWeight: 600 }}>
                      ✓ You have already completed this course
                    </div>
                  )}
                  <button className="cp-btn cp-btn-primary" onClick={() => setScreen("module")}>
                    {isAlreadyCompleted ? "Review Course →" : "Start Course →"}
                  </button>
                </div>
              ) : (
                <>
                  <div className="cp-module-header">
                    <div className="cp-module-tag">Module {activeModule.number} of {totalModules}</div>
                    <div className="cp-module-title">{activeModule.title}</div>
                    <div className="cp-module-subtitle">
                      {phase === "scenario" ? "Scenario" : phase === "quiz" ? "Knowledge Check" : `Slide ${slideIdx + 1} of ${totalSlides}`}
                    </div>
                  </div>

                  <div className="cp-slide-dots">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                      <div key={i} className={`cp-dot ${i < slideProgress ? "done" : i === slideProgress ? "active" : ""}`} />
                    ))}
                  </div>

                  {phase === "scenario" && (
                    <div className="cp-content cp-anim" key={`scenario-${activeModuleIdx}`}>
                      <div className="cp-scenario">
                        <div className="cp-scenario-label">🎬 Scenario — Meet {activeModule.scenario.character}</div>
                        <div className="cp-scenario-text">{activeModule.scenario.text}</div>
                        <div className="cp-scenario-prompt">{activeModule.scenario.prompt}</div>
                      </div>
                      <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6 }}>
                        Work through this module to find out the right approach. Let's go.
                      </p>
                    </div>
                  )}

                  {phase === "slides" && (
                    <div className="cp-content" key={`slide-${activeModuleIdx}-${slideIdx}`}>
                      <h2 className="cp-slide-heading">{activeModule.slides[slideIdx].heading}</h2>
                      <SlideContent slide={activeModule.slides[slideIdx]} />
                    </div>
                  )}

                  {phase === "quiz" && (
                    <Quiz
                      key={`quiz-${activeModuleIdx}`}
                      quiz={activeModule.quiz}
                      answered={quizAnswered}
                      onAnswer={(i) => setQuizAnswers(prev => ({ ...prev, [activeModuleIdx]: i }))}
                    />
                  )}

                  <div className="cp-nav-buttons">
                    <button className="cp-btn cp-btn-ghost" onClick={handlePrev}
                      disabled={phase === "scenario" && activeModuleIdx === 0}>
                      ← Back
                    </button>
                    <button
                      className={`cp-btn ${canGoNext ? (activeModuleIdx === totalModules - 1 && phase === "quiz" && quizAnswered !== null ? "cp-btn-success" : "cp-btn-primary") : "cp-btn-ghost"}`}
                      onClick={handleNext}
                      disabled={!canGoNext}>
                      {phase === "quiz" && quizAnswered !== null && activeModuleIdx === totalModules - 1
                        ? "Finish Course ✓"
                        : phase === "quiz" && quizAnswered === null
                        ? "Answer to continue"
                        : "Next →"}
                    </button>
                  </div>
                </>
              )}
            </main>
          </div>
        )}
      </div>
    </>
  );
}
